import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AppShell, PageHeader } from '@/components/AppShell';
import { api } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { VEHICLE_LABELS, fmtDate } from '@/lib/format';
import { ConfirmModal } from '@/components/ConfirmModal';
import { RideRating } from '@/components/RideRating';
import { Navigation, CheckCircle2, Trash2, ChevronRight, Clock, Star } from 'lucide-react';

export default function Rides() {
  const navigate = useNavigate();
  const t = useT();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [rateRide, setRateRide] = useState(null);

  const load = () => api.listRides().then((l) => { setRides(l); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    setConfirmId(null);
    try { await api.deleteRide(id); toast.success(t('r_deleted')); setRides((r) => r.filter((x) => x.id !== id)); }
    catch { toast.error(t('r_del_err')); }
  };

  return (
    <AppShell>
      <PageHeader title={t('r_title')} subtitle={t('r_sub')} />
      {loading && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('loading')}</p>}
      {!loading && rides.length === 0 && (
        <div className="rounded-2xl p-6 border text-center" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <Clock size={28} style={{ color: 'var(--text-muted)' }} className="mx-auto mb-2" />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('r_empty')}</p>
        </div>
      )}
      <div className="space-y-2">
        {rides.map((r) => (
          <div key={r.id} className="rounded-2xl p-3 border flex items-center justify-between" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="ride-history-item">
            <button onClick={() => navigate(`/app/ride/${r.id}`)} className="flex items-center gap-3 flex-1 text-left">
              {r.status === 'completed' ? <CheckCircle2 size={22} style={{ color: 'var(--success)' }} /> : <Navigation size={22} style={{ color: 'var(--primary)' }} />}
              <div>
                <div className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{VEHICLE_LABELS[r.vehicle_type] || 'Ride'} · <span className="tabular-nums">{r.public_ride_id}</span></div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{fmtDate(r.started_at)} · {r.status === 'completed' ? t('r_arrived') : r.status}</div>
              </div>
            </button>
            <div className="flex items-center gap-1">
              {r.status === 'completed' && (
                <button onClick={() => setRateRide({ ride_id: r.id, plate: r.plate_number || null, vehicle_type: r.vehicle_type })} className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ color: 'var(--warning)' }} aria-label={t('rr_rate')} data-testid="rate-ride-button"><Star size={18} /></button>
              )}
              <button onClick={() => setConfirmId(r.id)} className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ color: 'var(--danger)' }} aria-label="Delete ride" data-testid="delete-ride-button"><Trash2 size={18} /></button>
              <ChevronRight size={18} style={{ color: 'var(--text-muted)' }} />
            </div>
          </div>
        ))}
      </div>
      <ConfirmModal
        open={confirmId != null}
        onClose={() => setConfirmId(null)}
        title={t('r_del_q')}
        description={t('r_del_b')}
        cancelLabel={t('cancel')}
        confirmLabel={t('r_del')}
        onConfirm={() => del(confirmId)}
        danger
        confirmTestId="confirm-delete-ride-button"
      />
      <RideRating
        open={!!rateRide}
        ride={rateRide}
        onClose={() => setRateRide(null)}
        onSubmitted={() => setRateRide(null)}
      />
    </AppShell>
  );
}
